import {
  defineComponent,
} from 'vue';

// Modules
import { inlineLightbox } from '/src/ts/modules/lightbox.ts';

// Components
import ProductPrice from '/src/vue/components/ProductPrice.ts';
import ProductSurcharge from '/src/vue/components/ProductSurcharge.ts';
import ProductBulkPriceQuantity from '/src/vue/components/ProductBulkPriceQuantity.ts';
import ProductSalePriceQuantity from '/src/vue/components/ProductSalePriceQuantity.ts';

export default defineComponent({
  delimiters: ['${', '}'],
  components: {
    ProductPrice,
    ProductSurcharge,
    ProductBulkPriceQuantity,
    ProductSalePriceQuantity,
  },
  props: {
    sku: {
      type: String,
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    salePrice: {
      type: String,
      default: null,
    },
    isSos: {
      type: Boolean,
      default: false,
    },
    bulkPriceQuantity: {
      type: Number,
      default: null,
    },
    salePriceQuantity: {
      type: Number,
      default: null,
    },
    isOnSale: {
      type: Boolean,
      default: false,
    },
    isAvailableForPurchase: {
      type: Boolean,
      default: true,
    },
    isPacksOfTwo: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      quantity: this.isPacksOfTwo ? 2 : 1,
      isLoadingStockStatus: true,
    };
  },
  computed: {
    isCustomer() {
      return this.$store.getters.statuses.isCustomer;
    },
    isStopped() {
      return this.$store.getters.statuses.isStopped;
    },
    isInMaintenanceMode() {
      return this.$store.getters.statuses.isInMaintenanceMode;
    },
    isInStock() {
      return this.$store.getters.variantIsInStock(this.sku);
    },
    hasStockStatusFetchError() {
      return this.$store.getters.variantHasStockStatusFetchError(this.sku);
    },
    isCustomerWithSaleAccess() {
      return this.$store.getters.statuses.isCustomerWithSaleAccess;
    },
    showIsOnSale() {
      return this.isCustomerWithSaleAccess && this.isOnSale;
    },
    quantityStep() {
      return this.isPacksOfTwo ? 2 : 1;
    },
  },
  watch: {
    isCustomer() {
      this.checkBulkPriceQuantity();
      this.checkSalePriceQuantity();
      this.getIsInStock();
    },
    isStopped() {
      this.checkBulkPriceQuantity();
      this.checkSalePriceQuantity();
    },
  },
  mounted() {
    if (!this.isSos) {
      this.checkBulkPriceQuantity();
      this.checkSalePriceQuantity();
      this.getIsInStock();
    }
    if (this.isSos) {
      inlineLightbox();
    }
  },
  methods: {
    hasPricingAccess() {
      return this.isCustomer && !this.isStopped;
    },
    /**
     * Redirects user to a variant URL
     * @param $event
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    changeVariant($event: any) {
      // Get the variant ID
      const variantId = $event.target.value;
      // Get current URL
      const urlParams = new URLSearchParams(window.location.search);
      // Set variant param value
      urlParams.set('variant', variantId);
      // Redirect user to new path
      window.location.search = urlParams.toString();
    },
    checkBulkPriceQuantity() {
      if (!this.hasPricingAccess()) {
        return;
      }
      // If bulkPriceQuantity prop exists
      if (this.bulkPriceQuantity && this.$refs.productBulkPriceQuantityRef) {
        this.$refs.productBulkPriceQuantityRef.checkBulkPriceQuantity(this.quantity);
        if (this.quantity >= this.bulkPriceQuantity) {
          this.$refs.productPriceRef.getBulkPrice(this.quantity);
        }
      }
    },
    checkSalePriceQuantity() {
      if (!this.hasPricingAccess()) {
        return;
      }
      // If salePriceQuantity prop exists
      if (this.salePriceQuantity && this.$refs.productSalePriceQuantityRef) {
        this.$refs.productSalePriceQuantityRef.checkSalePriceQuantity(this.quantity);
      }
    },
    async getIsInStock() {
      if (!this.hasPricingAccess()) {
        return;
      }
      // Prep the payload
      const { sku } = this;
      const payload = {
        sku,
      };
      this.isLoadingStockStatus = true;
      // Send payload
      await this.$store.dispatch('getVariantStockStatus', payload);
      this.isLoadingStockStatus = false;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    qtyUp() {
      this.quantity = this.quantity + this.quantityStep;
      this.checkSalePriceQuantity();
      this.checkBulkPriceQuantity();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    qtyDown() {
      if(this.quantity > this.quantityStep) {
        this.quantity = Math.max(this.quantity - this.quantityStep, this.quantityStep);
        this.checkSalePriceQuantity();
        this.checkBulkPriceQuantity();
      }
    },
  },
  template: '#t-product-purchase-cta',
});
